import React, { PropsWithChildren, useEffect } from "react";
import { IControlledMutator, Nullable } from "sonobello.utilities.react";

import { GetCurrentStep, Step } from "../../../types/Step";
import IPathObserver from "../../Routing/Types/IPathObserver";
import { IPatient } from "../../Types/Entities/Patient";

export interface ICalendarPrerequisitesRecord {
  /** Flag indicating if the prerequisites to obtain a booking calendar are met. */
  isCalendarPrerequisiteMet: boolean;
}

export interface IOpsCalendarControllerProps
  extends IControlledMutator<Nullable<ICalendarPrerequisitesRecord>>,
    IPathObserver {
  /** The props of {@link IPatient} that are required to determine if the app can allow booking. */
  patient: Pick<IPatient, "id" | "isPreviouslyQualified">;
}

/** Suspends rendering children until the prerequisites for the calendar are met.
 * @remarks The prerequisites for accurate OPS calendar acquisition are knowing that a guest exists and has submitted
 * their medical information. We use the 'current step' as a alternate means of asserting this is complete.
 */
const OpsCalendarController: React.FC<PropsWithChildren<IOpsCalendarControllerProps>> = ({
  patient,
  pathName,
  value,
  onChange,
  children
}) => {
  useEffect(() => {
    const currentStep = GetCurrentStep(pathName);
    if (!value?.isCalendarPrerequisiteMet) {
      onChange({
        isCalendarPrerequisiteMet: Boolean(
          (patient.id && (currentStep === Step.scheduling || currentStep === Step.medicalGeneral)) ||
            patient.isPreviouslyQualified
        )
      });
    }
  }, [patient.id, patient.isPreviouslyQualified, pathName, value?.isCalendarPrerequisiteMet]);

  return value?.isCalendarPrerequisiteMet ? <>{children}</> : null;
};

export default OpsCalendarController;
